import { Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/atlas/Fields";
import { TimeField } from "@/components/events/TimeField";
import { usePositions } from "@/lib/positions";
import { useLang } from "@/lib/i18n";

const CATS = ["hospitality", "kitchen", "operations", "custom"];

export const PositionSelect = ({ value, onChange, testid, allowEmpty }) => {
  const { positions, label } = usePositions();
  const { t } = useLang();
  return (
    <NativeSelect value={value} onChange={onChange} testid={testid}>
      {allowEmpty && <option value="">—</option>}
      {CATS.map((c) => {
        const list = positions.filter((p) => p.category === c);
        if (!list.length) return null;
        return (
          <optgroup key={c} label={t(`cat.${c}`)}>
            {list.map((p) => <option key={p.key} value={p.key}>{label(p.key)}</option>)}
          </optgroup>
        );
      })}
    </NativeSelect>
  );
};

export const PositionsEditor = ({ value, onChange }) => {
  const { t } = useLang();
  const update = (i, k, v) => onChange(value.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
  const last = value[value.length - 1];
  const add = () => onChange([...value, { position_key: "server", required: 1, start_time: last?.start_time || "18:00", end_time: last?.end_time || "23:00", instructions: "" }]);
  return (
    <div className="space-y-3" data-testid="positions-editor">
      <div className="hidden md:grid grid-cols-[1.2fr_70px_190px_190px_1.2fr_40px] gap-3 eyebrow px-1">
        <span>{t("c.position")}</span><span>{t("f.qty")}</span><span>{t("f.start")}</span><span>{t("f.end")}</span><span>{t("f.posInstructions")}</span><span />
      </div>
      {value.map((p, i) => (
        <div key={p.id || i} className="grid grid-cols-2 md:grid-cols-[1.2fr_70px_190px_190px_1.2fr_40px] gap-3 p-3 md:p-1 border md:border-0 border-black/[0.06]" data-testid={`position-row-${i}`}>
          <div className="col-span-2 md:col-span-1"><PositionSelect value={p.position_key} onChange={(v) => update(i, "position_key", v)} testid={`position-key-${i}`} /></div>
          <Input data-testid={`position-qty-${i}`} type="number" min={1} value={p.required} onChange={(e) => update(i, "required", Math.max(1, parseInt(e.target.value || "1", 10)))} className="h-10 rounded-none mono col-span-2 md:col-span-1" />
          <div className="col-span-2 md:col-span-1 md:col-start-3 md:row-start-1 min-w-0"><TimeField testid={`position-start-${i}`} value={p.start_time} onChange={(v) => update(i, "start_time", v)} /></div>
          <div className="col-span-2 md:col-span-1 md:col-start-4 md:row-start-1 min-w-0"><TimeField testid={`position-end-${i}`} value={p.end_time} onChange={(v) => update(i, "end_time", v)} /></div>
          <Input data-testid={`position-instructions-${i}`} value={p.instructions} onChange={(e) => update(i, "instructions", e.target.value)} placeholder={t("f.posInstructions")} className="h-10 rounded-none col-span-2 md:col-span-1 md:col-start-5 md:row-start-1" />
          <button type="button" data-testid={`position-remove-${i}`} onClick={() => onChange(value.filter((_, j) => j !== i))} className="press h-10 grid place-items-center text-neutral-400 hover:text-red-600 md:col-start-6 md:row-start-1">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button type="button" data-testid="add-position-btn" onClick={add} className="press w-full border border-dashed border-black/20 py-3 text-sm text-neutral-600 hover:border-[#C5A059] hover:text-black flex items-center justify-center gap-2">
        <Plus className="w-4 h-4" /> {t("f.addPosition")}
      </button>
    </div>
  );
};
